import {Autocomplete, Button, Collapse, Grid, Paper, Tooltip} from "@mui/material";
import React, {MouseEventHandler, useState} from "react";
import {FilterAlt, Replay, Search} from "@mui/icons-material";
import {StyledTextField} from "./StyledTextField";
import {GenericComponentModel} from "../../api/types/generic";
import {DateTimePicker} from "@mui/x-date-pickers";
import CustomSwitch from "./CustomSwitch";
import {useTranslation} from "react-i18next";
import NumberInput from "../../../../util/component/NumberInput";
import {FormAutocomplete} from "../form-fields";

const SearchFilters = ({
                           handleClickSearch,
                           handleClickResetSearch,
                           filtersOutOfCollapse,
                           filtersInCollapse
                       }: {
    handleClickSearch: MouseEventHandler<HTMLButtonElement>,
    handleClickResetSearch: MouseEventHandler<HTMLButtonElement>,
    filtersOutOfCollapse: GenericComponentModel[],
    filtersInCollapse: GenericComponentModel[]
}) => {

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use State
    const [filterExpanded, setFilterExpanded] = useState<boolean>(false);

    const component = (filter: any) => {
        switch (filter.type) {
            case "TextField": {
                return <StyledTextField name={filter.name}
                                        value={filter.value}
                                        label={filter.label}
                                        fullWidth
                                        onChange={({target: {value}}) => filter.handleChange(value)}
                />;
            }
            case "NumberInput": {
                return <NumberInput label={filter.label}
                                    onChange={(value: number) => filter.handleChange(value)}
                />;
            }
            case "Autocomplete": {
                return <Autocomplete
                    options={filter.options}
                    isOptionEqualToValue={(option, value) => option?.code ? option.code === value?.code : option === value}
                    value={filter.value}
                    onChange={(e, value) => {
                        filter.handleChange(value);
                    }}
                    renderInput={(params) =>
                        <StyledTextField
                            {...params}
                            label={filter.label}
                            size="small"
                            fullWidth
                            variant="standard"
                        />
                    }
                />;
            }
            case "AutocompleteM": {
                /*                return <Autocomplete
                                    multiple={true}
                                    options={filter.options}
                                    isOptionEqualToValue={(option, value) => option?.code ? option.code === value?.code : option === value}
                                    value={filter.value}
                                    onChange={(e, value) => {
                                        filter.handleChange(value);
                                    }}
                                    renderInput={(params) =>
                                        <StyledTextField
                                            {...params}
                                            label={filter.label}
                                            size="small"
                                            fullWidth
                                            variant="standard"
                                        />
                                    }
                                />;*/
                return <FormAutocomplete
                    name={filter.name}
                    value={filter.value}
                    label={filter.label}
                    options={filter.options}
                    outOfControl={true}
                    handleChange={(value: any) => {
                        filter.handleChange(value);
                    }}
                    multiple={true}
                    isOptionEqualToValue={(option, value) => option?.code ? option.code === value?.code : option === value}
                />
            }
            case "Date": {
                return <DateTimePicker
                    value={filter.value || null}
                    disableOpenPicker={!filterExpanded}
                    onChange={(value) => {
                        filter.handleChange(value);
                    }}
                    inputFormat={"L LT" || ""}
                    renderInput={(params) =>
                        <StyledTextField
                            {...params}
                            label={filter.label}
                            fullWidth
                        />
                    }
                />;
            }
            case "Switch": {
                return <CustomSwitch
                    value={filter.value}
                    handleChange={(value: boolean) => {
                        filter.handleChange(value);
                    }}
                    label={filter.label}
                    readOnly={false}
                />;
            }
        }
    };

    //Gestiamo i click sul reset e l'invio chiudendo sempre i filtri avanzati se aperti
    const resetClickHandler = (e: any) => {
        if (filterExpanded) {
            setFilterExpanded(!filterExpanded)
        }
        handleClickResetSearch(e)
    }

    const searchClickHandler = (e: any) => {
        e.preventDefault();
        if (filterExpanded) {
            setFilterExpanded(!filterExpanded)
        }
        handleClickSearch(e)
    }

    return (
        <form onSubmit={(e: any) => searchClickHandler(e)}>
            <Paper elevation={1} sx={{p: 2, m: 2}}>
                <Grid container columnSpacing={5} alignItems="center">
                    {/* !IMPORTANT: filtersOutOfCollapse.length < 4 */}
                    {filtersOutOfCollapse.map((filter: GenericComponentModel, index: number) => (
                        <Grid item xs={12} md={3} key={index}>
                            {filter.hide ? null : component(filter)}
                        </Grid>
                    ))}
                    {/* sulla riga finale lente di ricerca e filtro collapse*/}
                    <Grid item
                          xs={12}
                          xl={filtersOutOfCollapse?.length === 1 ? 9 : filtersOutOfCollapse?.length === 2 ? 6 : 3}
                          sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              alignItems: "center"
                          }}
                    >
                        <Tooltip title="Reset"
                                 enterDelay={1000}
                                 enterNextDelay={1000}
                                 disableInteractive
                        >
                            <Button onClick={(e) => resetClickHandler(e)}
                                    variant={"text"}
                                    sx={{marginRight: "15px"}}
                            >
                                <Replay/> {t("pricing:actions.reset")}
                            </Button>
                        </Tooltip>
                        {filtersInCollapse.length > 0 &&
                            <Tooltip
                                title={filterExpanded ? t("pricing:actions.close") : t("pricing:actions.filter")}
                                enterDelay={1000}
                                enterNextDelay={1000}
                                disableInteractive
                            >
                                <Button onClick={() => setFilterExpanded(!filterExpanded)}
                                        variant={"text"}
                                        color="accent"
                                        sx={{marginRight: "15px"}}>
                                    <FilterAlt/> {filterExpanded ? t("pricing:actions.close") : t("pricing:actions.filter")}
                                </Button>
                            </Tooltip>}
                        <Tooltip title="Search"
                                 enterDelay={1000}
                                 enterNextDelay={1000}
                                 disableInteractive
                        >
                            <Button type="submit"
                                    size={"icon"}
                            >
                                <Search/>
                            </Button>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Collapse in={filterExpanded} timeout="auto" sx={{width: "100%"}}>
                    <Grid container columnSpacing={5} rowSpacing={2} pt={2}>
                        {filtersInCollapse
                            .map((filter: any, index: number) => (
                                <Grid item xs={12} md={3} key={index}>
                                    {filter.hide ? null : component(filter)}
                                </Grid>
                            ))}
                    </Grid>
                </Collapse>
            </Paper>
        </form>
    );
};

export default SearchFilters;